import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '../../utils/axios';
import { userLogin } from './authSlice';

export const fetchMyCompanies = createAsyncThunk(
  'company/fetchMyCompanies',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get('/api/companies-context/me');
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || err.message);
    }
  }
);

const initialActive = (() => {
  const stored = localStorage.getItem('activeCompanyId');
  return stored ? parseInt(stored) : null;
})();
const initialProduct = localStorage.getItem('activeProduct') || null;

const companySlice = createSlice({
  name: 'company',
  initialState: {
    list: [],
    activeCompanyId: initialActive,
    activeProduct: initialProduct,  // 'press_review' | 'theatrical_dcp' | null
    loading: false,
    hasFetched: false,
    error: null,
  },
  reducers: {
    setActiveCompany: (state, action) => {
      state.activeCompanyId = action.payload;
      // Switching company resets product selection
      state.activeProduct = null;
      localStorage.removeItem('activeProduct');
      if (action.payload) {
        localStorage.setItem('activeCompanyId', String(action.payload));
      } else {
        localStorage.removeItem('activeCompanyId');
      }
    },
    setActiveProduct: (state, action) => {
      state.activeProduct = action.payload;
      if (action.payload) {
        localStorage.setItem('activeProduct', action.payload);
      } else {
        localStorage.removeItem('activeProduct');
      }
    },
    clearCompanyContext: (state) => {
      state.list = [];
      state.activeCompanyId = null;
      state.activeProduct = null;
      localStorage.removeItem('activeCompanyId');
      localStorage.removeItem('activeProduct');
    },
  },
  extraReducers: (builder) => {
    builder
      // On fresh login, wipe any in-memory tenant context (localStorage
      // is cleared in authSlice). Pre-v10.17.14 this was keyed on the
      // string 'auth/userLogin/fulfilled' — a typo, since the thunk is
      // declared `createAsyncThunk('auth/login', …)` and the real action
      // type is 'auth/login/fulfilled'. The case never matched, so a
      // Redux state from a prior in-page session (HMR, fast re-login)
      // survived the login event and the dashboard rendered for the
      // wrong company. Use `.type` so any future rename catches itself.
      .addCase(userLogin.fulfilled, (state) => {
        state.activeCompanyId = null;
        state.activeProduct = null;
        state.list = [];
        state.hasFetched = false;
      })
      .addCase('auth/logout', (state) => {
        state.activeCompanyId = null;
        state.activeProduct = null;
        state.list = [];
        state.hasFetched = false;
      })
      .addCase(fetchMyCompanies.pending, (state) => { state.loading = true; })
      .addCase(fetchMyCompanies.fulfilled, (state, action) => {
        state.loading = false;
        state.hasFetched = true;
        state.list = action.payload;
        // Note: auto-select is now handled in CompanyPicker (so superadmins always see the picker
        // and can choose between Operator Panel and a tenant company)
        // If active company is no longer in list, clear it
        if (state.activeCompanyId && !action.payload.find(c => c.id === state.activeCompanyId)) {
          state.activeCompanyId = null;
          localStorage.removeItem('activeCompanyId');
        }
      })
      .addCase(fetchMyCompanies.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { setActiveCompany, setActiveProduct, clearCompanyContext } = companySlice.actions;
export default companySlice.reducer;
